// The A/B Testing passcode prompt. Mounted once in App.js; utils/abOtp.js calls
// into it (ensureAbOtp, and the 403 AB_OTP_REQUIRED retry), so any page can ask
// for the passcode without owning a modal of its own.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { registerAbOtpPrompt, verifyAbOtp } from '../../utils/abOtp';
import './abTesting.css';

const LENGTH = 6;
const ACTION_COPY = {
    create: 'create this A/B test',
    edit: 'edit this A/B test',
    delete: 'delete this A/B test',
};

export default function AbOtpPromptHost({ app_url }) {
    const [request, setRequest] = useState(null); // { campaignId, action, resolve }
    const [digits, setDigits] = useState(Array(LENGTH).fill(''));
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const inputs = useRef([]);
    const pending = useRef(null);

    const prompt = useCallback((campaignId, action) => new Promise((resolve) => {
        // A second ask while one is open (e.g. two saves racing) joins the first.
        if (pending.current) {
            const prev = pending.current;
            pending.current = { ...prev, waiters: [...prev.waiters, resolve] };
            return;
        }
        pending.current = { campaignId, action, waiters: [resolve] };
        setDigits(Array(LENGTH).fill(''));
        setError('');
        setRequest({ campaignId, action });
    }), []);

    useEffect(() => registerAbOtpPrompt(prompt), [prompt]);

    useEffect(() => {
        if (request) setTimeout(() => inputs.current[0]?.focus(), 50);
    }, [request]);

    const finish = (ok) => {
        const waiters = pending.current?.waiters || [];
        pending.current = null;
        setRequest(null);
        setBusy(false);
        waiters.forEach((resolve) => resolve(ok));
    };

    const code = digits.join('');

    const submit = async (e) => {
        e?.preventDefault();
        if (code.length !== LENGTH || busy || !request) return;
        setBusy(true);
        setError('');
        try {
            const data = await verifyAbOtp(app_url, { campaignId: request.campaignId, action: request.action, otp: code });
            if (data?.success) return finish(true);
            setError(data?.message || 'That passcode is not correct.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Could not verify the passcode. Try again.');
        }
        setDigits(Array(LENGTH).fill(''));
        setBusy(false);
        setTimeout(() => inputs.current[0]?.focus(), 30);
    };

    const setDigit = (index, value) => {
        const clean = value.replace(/\D/g, '');
        if (clean.length > 1) {
            // Pasted the whole code into one box.
            const next = clean.slice(0, LENGTH).split('');
            setDigits([...next, ...Array(LENGTH - next.length).fill('')]);
            inputs.current[Math.min(next.length, LENGTH - 1)]?.focus();
            return;
        }
        setDigits((prev) => prev.map((d, i) => (i === index ? clean : d)));
        if (clean && index < LENGTH - 1) inputs.current[index + 1]?.focus();
    };

    const onKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
        if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
        if (e.key === 'ArrowRight' && index < LENGTH - 1) inputs.current[index + 1]?.focus();
    };

    return (
        <Modal show={!!request} onHide={() => !busy && finish(false)} centered backdrop="static" dialogClassName="ab-otp-modal">
            <form onSubmit={submit} data-testid="ab-otp-modal">
                <Modal.Header closeButton={!busy}>
                    <Modal.Title as="h5" style={{ fontWeight: 600 }}>A/B Testing passcode</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="ab-otp-lead">
                        Enter the 6-digit passcode to {ACTION_COPY[request?.action] || ACTION_COPY.edit}.
                        Ask your platform admin if you don&apos;t have it.
                    </p>
                    <div className="ab-otp-digits" role="group" aria-label="Passcode">
                        {digits.map((d, i) => (
                            <input
                                key={i}
                                ref={(el) => { inputs.current[i] = el; }}
                                className={`ab-otp-digit${error ? ' ab-otp-digit-error' : ''}`}
                                value={d}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={LENGTH}
                                aria-label={`Digit ${i + 1}`}
                                data-testid={`ab-otp-digit-${i}`}
                                disabled={busy}
                                onChange={(e) => setDigit(i, e.target.value)}
                                onKeyDown={(e) => onKeyDown(i, e)}
                            />
                        ))}
                    </div>
                    {error && <div className="ab-otp-error" role="alert">{error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="ab-otp-cancel btn-height" onClick={() => finish(false)} disabled={busy}>Cancel</button>
                    <button type="submit" className="sxi_primary_blue_btn btn-height ab-otp-submit" disabled={busy || code.length !== LENGTH} data-testid="ab-otp-submit">
                        {busy ? 'Verifying…' : 'Continue'}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
